import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

export interface TripsSummary {
  savedCount: number;
  lastTrip: { city: string; date: string } | null;
}

// Parse a Supabase ISO timestamp as UTC even when the `Z` suffix is
// missing — Supabase usually returns one, but stripped-suffix dates
// would otherwise be interpreted as local time and shift the
// "May 27" badge to the next/prev day for users in non-zero offsets.
function parseUtcDate(raw: string): Date {
  return new Date(/[Zz]|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}Z`);
}

// Powers the compact Hero's two personalization badges (saved-trips
// count + last-trip city/date). One round-trip per mount; `count:
// 'exact'` returns total + first row in the same call. Falls back to
// `{savedCount: 0, lastTrip: null}` when signed-out, query fails, or
// nothing to show — consumers hide the badges rather than rendering
// a "0 saved trips" empty state.
//
// `enabled=false` skips the query entirely. Useful when the Hero is
// rendering in `full` mode where the pills don't exist — saves one
// Supabase round-trip on every landing-page render.
export function useTripsSummary(enabled = true): TripsSummary {
  const { user } = useAuth();
  const [summary, setSummary] = useState<TripsSummary>({ savedCount: 0, lastTrip: null });

  useEffect(() => {
    if (!enabled || !user?.id) {
      setSummary({ savedCount: 0, lastTrip: null });
      return;
    }

    let cancelled = false;
    (async () => {
      const { data, count, error } = await supabase
        .from('saved_searches')
        .select('destination_city_name, query, created_at', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (cancelled) return;
      if (error || !data) {
        setSummary({ savedCount: 0, lastTrip: null });
        return;
      }

      const row = data[0];
      const city =
        row?.destination_city_name?.trim() ||
        // Fallback for older rows that pre-date the VOY-411 enrichment
        // column — pull a readable city from the freeform query.
        (typeof row?.query?.destination === 'string'
          ? row.query.destination.replace(/\s*\([A-Z]{3}\).*$/, '').split(',')[0]?.trim()
          : undefined);

      // Prefer the planned travel date (query.departureDate) over the
      // save date — "Last: Lisbon, May 27" reads as "when am I going"
      // not "when did I last open the search card". Fall back to
      // created_at when departureDate is missing or unparseable.
      const rawDate =
        (typeof row?.query?.departureDate === 'string' ? row.query.departureDate : null) ||
        row?.created_at ||
        null;
      const parsed = rawDate ? parseUtcDate(rawDate) : null;
      const dateLabel = parsed && !isNaN(parsed.getTime())
        ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : null;

      setSummary({
        savedCount: count ?? 0,
        lastTrip: row && city && dateLabel ? { city, date: dateLabel } : null,
      });
    })();

    return () => { cancelled = true; };
  }, [enabled, user?.id]);

  return summary;
}
